import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ORG_TYPES = ["First Nations community-controlled organisation", "Traditional Owner or cultural authority", "Government", "University or research institution", "Non-government organisation", "Corporate organisation", "Funder or philanthropic organisation", "Other"];
const ENGAGEMENTS = ["Governance Gap Analysis", "Indigenous Data Sovereignty Review", "Cultural Governance Assurance", "Facilitated Workshop", "Research Partnership", "Licensed Bigibila Services and Outputs", "Pilot Project", "Confidential Discussion"];
const BUDGETS = ["Under $10,000", "$10,000–$25,000", "$25,000–$50,000", "$50,000–$100,000", "Over $100,000", "Budget not yet confirmed"];
const STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT", "National", "Other"];

const empty = {
  full_name: "", position: "", organisation: "", email: "", phone: "",
  organisation_type: ORG_TYPES[0], state_territory: "NSW",
  project_system: "", governance_problem: "", communities_affected: "",
  desired_outcome: "", decision_makers: "", commencement_date: "",
  budget_range: BUDGETS[5], engagement_type: ENGAGEMENTS[7],
  ack_no_licence: false, ack_no_disclosure: false, ack_accurate: false, ack_consent: false,
  website: "",
};

const ACKS = [
  ["ack_no_licence", "I understand that submitting this form does not grant access to or a licence for Bigibila intellectual property."],
  ["ack_no_disclosure", "I understand that Bigibila's internal codebook, decision rules, scoring architecture and methodological machinery are never disclosed under any engagement, NDA, licence, partnership or payment."],
  ["ack_accurate", "I confirm that the information supplied is accurate and that I am authorised to make this enquiry."],
  ["ack_consent", "I consent to Bigibila securely storing this information for assessment and contact purposes."],
];

const Field = ({ label, children }) => (
  <div>
    <label className="font-mono text-xs uppercase tracking-[0.2em] text-stone2 block mb-2">{label}</label>
    {children}
  </div>
);

export default function BriefingForm() {
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const required = ["full_name", "position", "organisation", "email", "phone", "state_territory", "project_system", "governance_problem", "communities_affected", "desired_outcome", "decision_makers", "commencement_date"];
    if (required.some((k) => !String(form[k]).trim())) {
      toast.error("Please complete all required fields.");
      return;
    }
    if (!(form.ack_no_licence && form.ack_no_disclosure && form.ack_accurate && form.ack_consent)) {
      toast.error("Please accept all acknowledgments.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/briefing-requests`, form);
      setDone(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Submission failed. Please review the form and try again.");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <section id="briefing" data-testid="briefing-confirmation" className="py-32 md:py-44 border-b border-line">
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-5xl tracking-tight mb-8">Thank you. Bigibila has received your <em className="text-ember">confidential briefing request.</em></h2>
          <p className="text-stone2 leading-relaxed mb-4">Your submission will be reviewed for strategic fit, cultural authority, scope and resourcing. Submission does not guarantee acceptance of an engagement or access to protected Bigibila intellectual property.</p>
          <p className="text-stone2 leading-relaxed">Bigibila will contact you using the details provided.</p>
        </div>
      </section>
    );
  }

  const inputCls = "field-dark w-full px-4 py-3 text-sm";

  return (
    <section id="briefing" data-testid="briefing-section" className="py-24 md:py-32 border-b border-line bg-surface1">
      <div className="max-w-7xl mx-auto px-4 md:px-8 mb-16">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-ochre mb-6">Confidential briefing</p>
        <h2 data-testid="briefing-heading" className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight max-w-4xl mb-8">
          Request a Confidential <em className="text-ember">Bigibila Briefing</em>
        </h2>
        <p className="text-stone2 leading-relaxed max-w-3xl mb-6">
          Bigibila provides confidential briefings for First Nations organisations, governments, universities, service providers, funders and institutional partners seeking First Nations-led governance analysis, Indigenous Data Sovereignty or licensed implementation.
        </p>
        <p className="text-bone border-l-2 border-ember pl-5 max-w-3xl text-sm">
          Submitting this form does not provide access to Bigibila's methodology or create a licence.
        </p>
      </div>

      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <div data-testid="cultural-authority-notice" className="border border-ember/40 bg-ink p-6 md:p-8 mb-12">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ochre mb-3">Cultural authority</p>
          <p className="text-bone text-sm leading-relaxed">
            Bigibila does not assume authority over Country, community knowledge or cultural decision-making. Any proposed engagement must identify the relevant cultural authorities, permissions and intended beneficiaries.
          </p>
        </div>

        <form onSubmit={submit} data-testid="briefing-form" className="space-y-6" autoComplete="off">
          <input type="text" name="website" value={form.website} onChange={set("website")} tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ position: "absolute", left: "-9999px", height: 0, width: 0, opacity: 0 }} />
          <div className="grid sm:grid-cols-2 gap-6">
            <Field label="Full name *"><input data-testid="bf-name" className={inputCls} value={form.full_name} onChange={set("full_name")} /></Field>
            <Field label="Position *"><input data-testid="bf-position" className={inputCls} value={form.position} onChange={set("position")} /></Field>
            <Field label="Organisation *"><input data-testid="bf-org" className={inputCls} value={form.organisation} onChange={set("organisation")} /></Field>
            <Field label="Email *"><input type="email" data-testid="bf-email" className={inputCls} value={form.email} onChange={set("email")} /></Field>
            <Field label="Phone *"><input data-testid="bf-phone" className={inputCls} value={form.phone} onChange={set("phone")} /></Field>
            <Field label="Organisation type *">
              <select data-testid="bf-orgtype" className={inputCls} value={form.organisation_type} onChange={set("organisation_type")}>{ORG_TYPES.map((o) => <option key={o}>{o}</option>)}</select>
            </Field>
            <Field label="State or territory *">
              <select data-testid="bf-state" className={inputCls} value={form.state_territory} onChange={set("state_territory")}>{STATES.map((o) => <option key={o}>{o}</option>)}</select>
            </Field>
            <Field label="Proposed commencement date *"><input type="date" data-testid="bf-date" className={inputCls} value={form.commencement_date} onChange={set("commencement_date")} /></Field>
            <Field label="Type of engagement sought *">
              <select data-testid="bf-engagement" className={inputCls} value={form.engagement_type} onChange={set("engagement_type")}>{ENGAGEMENTS.map((o) => <option key={o}>{o}</option>)}</select>
            </Field>
            <Field label="Available budget range *">
              <select data-testid="bf-budget" className={inputCls} value={form.budget_range} onChange={set("budget_range")}>{BUDGETS.map((o) => <option key={o}>{o}</option>)}</select>
            </Field>
          </div>
          <Field label="Project or system requiring review *"><textarea rows={3} data-testid="bf-project" className={`${inputCls} resize-none`} value={form.project_system} onChange={set("project_system")} /></Field>
          <Field label="Current governance problem *"><textarea rows={3} data-testid="bf-problem" className={`${inputCls} resize-none`} value={form.governance_problem} onChange={set("governance_problem")} /></Field>
          <Field label="First Nations communities or knowledge potentially affected *"><textarea rows={3} data-testid="bf-communities" className={`${inputCls} resize-none`} value={form.communities_affected} onChange={set("communities_affected")} /></Field>
          <Field label="Desired outcome *"><textarea rows={3} data-testid="bf-outcome" className={`${inputCls} resize-none`} value={form.desired_outcome} onChange={set("desired_outcome")} /></Field>
          <Field label="Intended decision-makers *"><input data-testid="bf-decisionmakers" className={inputCls} value={form.decision_makers} onChange={set("decision_makers")} /></Field>

          <div className="border border-line bg-ink p-6 space-y-4">
            {ACKS.map(([k, text], i) => (
              <label key={k} className="flex items-start gap-3 text-sm text-stone2 leading-relaxed cursor-pointer">
                <input type="checkbox" data-testid={`bf-ack-${i}`} checked={form[k]} onChange={set(k)} className="mt-1 accent-[#B44A2C]" />
                {text}
              </label>
            ))}
          </div>

          <button type="submit" disabled={sending} data-testid="briefing-submit-btn" className="font-mono text-sm uppercase tracking-[0.2em] px-10 py-4 bg-ember text-ink hover:bg-emberDeep transition-colors duration-300 disabled:opacity-60">
            {sending ? "Submitting…" : "Submit confidential request"}
          </button>

          <p data-testid="briefing-privacy" className="font-mono text-[11px] text-stone2 leading-relaxed max-w-3xl pt-4">
            Bigibila collects this information to assess potential engagements and respond to enquiries. Do not submit restricted cultural knowledge, child-protection records, health information, legal evidence, DFV records or other highly sensitive material through this form. Bigibila will establish an appropriate secure transfer process if such information is required.
          </p>
        </form>
      </div>
    </section>
  );
}
